/** Хранилище флагов онбординга по пользователю: fk_sms_<kind>:<sub из JWT>. Все операции в try/catch
 * (приватный режим, отключённый storage, node-тесты без localStorage). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type OnboardingKind = 'tour_done' | 'tour_step' | 'checklist_hidden' | 'guide_hidden' | 'nav';

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  // atob отдаёт latin1-строку; sub обычно ASCII, но на всякий случай декодируем как UTF-8.
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** `sub` из payload JWT; без токена или при ошибке — 'anon'. */
export function userKeyFromToken(token: string | null): string {
  if (!token) return 'anon';
  try {
    const parts = token.split('.');
    if (parts.length < 2) return 'anon';
    const payload = JSON.parse(decodeBase64Url(parts[1])) as { sub?: unknown };
    const sub = payload?.sub;
    if (typeof sub === 'string' && sub.trim()) return sub.trim();
    if (typeof sub === 'number') return String(sub);
    return 'anon';
  } catch {
    return 'anon';
  }
}

export function storageKey(kind: OnboardingKind, userKey: string): string {
  return `fk_sms_${kind}:${userKey}`;
}

/** localStorage браузера или undefined (SSR/тесты/запрет доступа). Вызывать только в эффектах/обработчиках. */
export function safeStorage(): StorageLike | undefined {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    return localStorage;
  } catch {
    return undefined;
  }
}

export function readFlag(storage: StorageLike | undefined, key: string): boolean {
  try {
    return storage?.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function writeFlag(storage: StorageLike | undefined, key: string): void {
  try {
    storage?.setItem(key, '1');
  } catch {
    /* приватный режим — флаг не сохранится, это допустимо */
  }
}

export function readJson<T>(storage: StorageLike | undefined, key: string): T | null {
  try {
    const raw = storage?.getItem(key);
    return raw == null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export function writeJson(storage: StorageLike | undefined, key: string, value: unknown): void {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    /* см. writeFlag */
  }
}

export function remove(storage: StorageLike | undefined, key: string): void {
  try {
    storage?.removeItem(key);
  } catch {
    /* см. writeFlag */
  }
}
